import { Typography, type TypographyProps } from '@mui/material';
import type { Currency } from '@vy/types';
import type { ReactNode } from 'react';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { FormatStyle, generateNumberToDisplay } from './utils/generateNumberToDisplay';
import { VyTooltip } from '../../new-design/VyTooltip';

type NumberDisplayProps = Omit<TypographyProps, 'align'> & {
  children: number | ReactNode | string;
  currency?: Currency;
  animated?: boolean;
  align?: 'left' | 'center' | 'right' | 'unset';
  rounded?: boolean;
  fractionDigits?: number;
  formattingStyle?: FormatStyle;
};

export const NumberDisplay: React.FC<NumberDisplayProps> = ({
  children,
  currency,
  align = 'unset',
  formattingStyle = 'currency',
  fractionDigits,
  ...props
}) => {
  const { shouldDisplayTooltip, numberToDisplay } = generateNumberToDisplay(
    children,
    formattingStyle,
    currency,
    fractionDigits,
  );

  return (
    <Typography
      {...props}
      className={twMerge(
        'truncate max-w-min',
        props?.className,
        align === 'right' && 'ml-auto',
        align === 'center' && 'mx-auto',
        align === 'left' && 'mr-auto',
      )}
      sx={[
        {
          fontSize: 14,
          textAlign: align,
          ...props?.sx,
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      <VyTooltip tooltipContent={shouldDisplayTooltip ? children : null}>
        <span>{numberToDisplay}</span>
      </VyTooltip>
    </Typography>
  );
};
